/**
 * What an action is (WALLIYO-0069).
 *
 * Everything anyone does in Walliyo is an action: a client posting a job, a pro
 * sending a quote, staff issuing a refund, the system expiring a job nobody
 * answered. It is the EMSX model: creating a job is creating an order, a quote
 * is routing it, and selecting a quote is the fill.
 *
 * Three layers, one word each:
 * - **Action**: the thing done.
 * - **Feature enablement** (`../settings`): whether someone may see and take it.
 * - **Audit log**: the record that it was done.
 *
 * Every action that creates or changes something is audited, always. A read is
 * audited only when it is sensitive, such as revealing a phone number.
 *
 * On behalf of is not a separate action. Staff take the customer's own action
 * with an on-behalf marker, the way an OBO order is still the same order. Acting
 * for someone needs ACT_ON_BEHALF_STAFF_FEATURE_ENABLEMENT, and the action must
 * allow it.
 */

import type { SettingId } from "../settings/definitions";
import type { Status } from "../settings/types";

/** A customer feature that exists in SETTINGS. A typo is a compile error. */
export type UserFeatureId = Extract<SettingId, `${string}_USER_FEATURE_ENABLEMENT`>;

/** A staff feature that exists in SETTINGS. */
export type StaffFeatureId = Extract<SettingId, `${string}_STAFF_FEATURE_ENABLEMENT`>;

export type ActorType = "customer" | "staff" | "system";

/** `write` creates or changes something and is always audited. `read` only looks. */
export type ActionEffect = "read" | "write";

/** The audit log's areas, which the Audit tab filters on. */
export type ActionArea =
  | "account"
  | "access"
  | "onboarding"
  | "discovery"
  | "jobs"
  | "leads"
  | "messaging"
  | "reviews"
  | "payments"
  | "support"
  | "safety"
  | "settings"
  | "staff"
  | "audit";

/** What an action acts on. Recorded as the audit entry's target type. */
export type ActionTarget =
  | "user"
  | "client_profile"
  | "pro_profile"
  | "job"
  | "quote"
  | "conversation"
  | "message"
  | "review"
  | "platform_fee_payment"
  | "pro_subscription"
  | "plan_price"
  | "plan_promo"
  | "pro_discount"
  | "setting_value"
  /** An outage, and what the app tells people about it (WALLIYO-0132, #126). */
  | "incident"
  /** A rollout held at a share of customers (WALLIYO-0132, #132). */
  | "experiment"
  /** A pro who is not on the platform yet (WALLIYO-0132). */
  | "lead"
  /** A message to a group of people (WALLIYO-0132, #125). */
  | "campaign"
  | "rule_version"
  | "support_case"
  | "on_behalf_session"
  | "identity_verification"
  | "safety_report"
  | "police_referral"
  | "legal_hold"
  | "approval_request"
  | "sms_template"
  | "staff_member"
  | "staff_role"
  | "staff_team"
  | "staff_session"
  | "audit_log"
  | "catalog"
  // Human Resources (WALLIYO-0131). Each one is a thing an action is taken on,
  // not a screen: the employment record, a leave request, a vacancy, a month's
  // payroll, a review, a course.
  | "staff_employment"
  | "time_off_request"
  | "job_opening"
  | "job_candidate"
  | "payroll_run"
  | "performance_review"
  | "course";

/** Which of a customer's profiles takes the action. */
export type ActingProfile = "client" | "pro" | "either";

export type UserActionId = `${string}_USER_ACTION`;
export type StaffActionId = `${string}_STAFF_ACTION`;
export type SystemActionId = `${string}_SYSTEM_ACTION`;
export type ActionIdPattern = UserActionId | StaffActionId | SystemActionId;

/** What every action has, whoever takes it. */
export interface ActionBase<I extends ActionIdPattern = ActionIdPattern> {
  /** Permanent, readable identity: <VERB_OBJECT>_<USER|STAFF|SYSTEM>_ACTION. */
  actionId: I;
  /**
   * What it is called, in English: the source text the audit log and "who can
   * do this" show instead of the id (#185). A short verb phrase.
   */
  name: string;
  actorType: ActorType;
  area: ActionArea;
  effect: ActionEffect;
  target: ActionTarget;
  /** A read that is audited anyway, and whose details the Audit tab hides without the right feature. */
  sensitive: boolean;
  status: Status;
}

/** Something a client or pro does in the app. */
export interface UserAction<I extends UserActionId = UserActionId> extends ActionBase<I> {
  actorType: "customer";
  profile: ActingProfile;
  /**
   * The feature that must be on for the customer. `null` means always
   * available, e.g. cancelling your own job or deleting your account.
   */
  requiredFeature: UserFeatureId | null;
  /**
   * Whether staff may take it for the customer. Never for changing sign-in
   * details, reading their messages, writing reviews, or anything involving
   * their money.
   */
  onBehalfAllowed: boolean;
}

/** Something staff do in the console. */
export interface StaffAction<I extends StaffActionId = StaffActionId> extends ActionBase<I> {
  actorType: "staff";
  /**
   * The feature the staff member must hold. `null` only for what every staff
   * member does for themselves: signing in and out, their own console settings.
   */
  requiredFeature: StaffFeatureId | null;
  /**
   * Other features that also allow it: holding `requiredFeature` OR any of
   * these is enough. Empty for almost every action. Used where two rights
   * genuinely overlap, e.g. granting a low-sensitivity feature to a role is
   * open to both MANAGE_ROLES and ASSIGN_ROLES (ADR-0013 §2.2).
   */
  alsoAllowedBy: readonly StaffFeatureId[];
  /** Every staff change says why. */
  requiresReason: boolean;
}

/**
 * Something Walliyo does by itself: a schedule, a webhook, a consequence of
 * another action. `requiredFeature` is checked for the customer it affects,
 * e.g. lead notifications only go to pros with LEAD_NOTIFICATIONS on.
 */
export interface SystemAction<I extends SystemActionId = SystemActionId> extends ActionBase<I> {
  actorType: "system";
  requiredFeature: UserFeatureId | null;
}

export type Action = UserAction | StaffAction | SystemAction;

/** Whether an action is written to the audit log: every write, and sensitive reads. */
export function isAudited(action: Pick<Action, "effect" | "sensitive">): boolean {
  return action.effect === "write" || action.sensitive;
}

// ── Define functions: write what varies, the actor fixes the rest ─────────

type Optional = { sensitive?: boolean; status?: Status };

export function defineUserAction<const I extends UserActionId>(
  action: Omit<UserAction<I>, "actorType" | "sensitive" | "status"> & Optional,
): UserAction<I> {
  return {
    actionId: action.actionId,
    name: action.name,
    actorType: "customer",
    profile: action.profile,
    area: action.area,
    effect: action.effect,
    target: action.target,
    requiredFeature: action.requiredFeature,
    onBehalfAllowed: action.onBehalfAllowed,
    sensitive: action.sensitive ?? false,
    status: action.status ?? "active",
  };
}

export function defineStaffAction<const I extends StaffActionId>(
  action: Omit<StaffAction<I>, "actorType" | "requiresReason" | "sensitive" | "status" | "alsoAllowedBy"> &
    Optional & { alsoAllowedBy?: readonly StaffFeatureId[] },
): StaffAction<I> {
  return {
    actionId: action.actionId,
    name: action.name,
    actorType: "staff",
    area: action.area,
    effect: action.effect,
    target: action.target,
    requiredFeature: action.requiredFeature,
    alsoAllowedBy: action.alsoAllowedBy ?? [],
    requiresReason: action.effect === "write",
    sensitive: action.sensitive ?? false,
    status: action.status ?? "active",
  };
}

export function defineSystemAction<const I extends SystemActionId>(
  action: Omit<SystemAction<I>, "actorType" | "sensitive" | "status"> & Optional,
): SystemAction<I> {
  return {
    actionId: action.actionId,
    name: action.name,
    actorType: "system",
    area: action.area,
    effect: action.effect,
    target: action.target,
    requiredFeature: action.requiredFeature,
    sensitive: action.sensitive ?? false,
    status: action.status ?? "active",
  };
}
